import { Injectable } from '@angular/core';
import { Observable, EMPTY, concat, from, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import {
  AridosDashboardFacadeContract,
  AridosDashboardOrderDetails,
  AridosDashboardRow,
} from './aridos-dashboard.types';

@Injectable({ providedIn: 'root' })
export class AridosDashboardFacade implements AridosDashboardFacadeContract {
  readonly online$ = this.offlineStorage.online$;

  constructor(
    private readonly orderService: OrderService,
    private readonly offlineStorage: OfflineStorageService,
  ) {}

  isOnline(): boolean {
    return this.offlineStorage.isOnline();
  }

  loadSummary(fromDate: string, toDate: string): Observable<AridosDashboardRow[]> {
    const cacheKey = `dashboard:aridos:resumen_${fromDate}_${toDate}`;
    const cached = this.readArray(cacheKey);
    const network$ = this.orderService
      .getResumenDespacho(fromDate, toDate, cached.exists)
      .pipe(
        map((rows) => rows.map((row) => this.mapRow(row))),
        tap((rows) => this.write(cacheKey, rows)),
      );

    return concat(cached.exists ? of(cached.rows) : EMPTY, network$);
  }

  loadToday(date: string): Observable<AridosDashboardRow | null> {
    const cacheKey = `dashboard:aridos:resumen_hoy_${date}`;
    const cached = this.readSingle(cacheKey);
    const network$ = this.orderService.getResumenDespacho(date, date, cached.exists).pipe(
      map((rows) => (rows.length ? this.mapRow(rows[0]) : null)),
      tap((row) => this.write(cacheKey, row)),
    );

    return concat(cached.exists ? of(cached.row) : EMPTY, network$);
  }

  loadOrderDetails(): Observable<AridosDashboardOrderDetails[]> {
    return from(this.orderService.getAllOrders(false)).pipe(
      switchMap((orders$) => orders$),
      map((orders) =>
        (orders || []).map((order: any) => ({
          orderNumber: String(order.orderNumber ?? ''),
          ticket: String(order.tkt ?? ''),
          product: order.productDescription ?? '',
          quantity: order.scheduledAmount ?? '',
          deliveryTime: order.startTime ?? '',
        })),
      ),
    );
  }

  private mapRow(raw: any): AridosDashboardRow {
    const rejected = String(raw?.RECHAZADO ?? '').trim() !== '';
    const signedValue = String(raw?.FIRMADA ?? '').trim();
    const signed = signedValue !== '' && signedValue !== '0';
    const status = Number(raw?.STATUS_H ?? NaN);

    let state: AridosDashboardRow['state'] = 'desconocido';
    if (rejected) state = 'anulado';
    else if (status < 6 && status !== 0 && !signed) state = 'pendiente';
    else if (status >= 6 || status === 0 || signed) state = 'entregado';

    return {
      orderNumber: String(raw?.NUMERO_PEDID_H ?? ''),
      ticket: String(raw?.TKT_H ?? ''),
      deliveryDate: raw?.FECHA_PED_DATE_H ?? '',
      deliveryTime: raw?.HORA_INI_H ?? '',
      address: raw?.DIRECCION_ENTREGA_H ?? '',
      project: raw?.NOMBRE_PROYECTO_H ?? '',
      product: raw?.DESCRIPCION_PRODUCTO_H ?? '',
      quantity: raw?.CANTIDAD_PROGRAMADA_H ?? '',
      guideNumber: String(raw?.FOLIO_H ?? ''),
      week: String(raw?.NUMEROSEMANAISO ?? ''),
      state,
      guideState: rejected ? 'rejected' : signed ? 'signed' : 'unsigned',
    };
  }

  private readArray(key: string): { exists: boolean; rows: AridosDashboardRow[] } {
    const raw = localStorage.getItem(key);
    if (raw === null) return { exists: false, rows: [] };
    try {
      const parsed = JSON.parse(raw);
      return {
        exists: true,
        rows: Array.isArray(parsed) ? parsed : [],
      };
    } catch {
      return { exists: true, rows: [] };
    }
  }

  private readSingle(key: string): { exists: boolean; row: AridosDashboardRow | null } {
    const raw = localStorage.getItem(key);
    if (raw === null) return { exists: false, row: null };
    try {
      return { exists: true, row: JSON.parse(raw) as AridosDashboardRow | null };
    } catch {
      return { exists: true, row: null };
    }
  }

  private write(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // El dashboard continúa funcionando aunque el almacenamiento no esté disponible.
    }
  }
}
